import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // login | 2fa | forgot
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [code, setCode] = useState('');
  const [twoFaMethod, setTwoFaMethod] = useState('email'); // 'email' | 'totp'
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (res.data.requires_2fa) {
        setTempToken(res.data.temp_token);
        const method = res.data.method || 'totp';
        setTwoFaMethod(method);
        setMode('2fa');
        toast.info(method === 'email' ? 'We emailed you a 6-digit login code' : 'Enter your authenticator code');
      } else {
        setUser(res.data.user);
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-2fa`, { temp_token: tempToken, code }, { withCredentials: true });
      setUser(res.data.user);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/resend-2fa`, { temp_token: tempToken }, { withCredentials: true });
      setTempToken(res.data.temp_token);
      setCode('');
      toast.success('A new code has been sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend the code');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If an account exists, a reset link has been sent to your email.');
      setMode('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="w-full max-w-md p-8 animate-fade-in">
        <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }} data-testid="login-title">
              <span className="text-[#00FF99]">LCBSE</span> CRM
            </h1>
            <p className="text-[#808080] text-sm mt-1">Life Can Be So Easy</p>
          </div>

          {mode === 'login' && (
            <>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Email</Label>
                  <Input data-testid="login-email" value={email} onChange={e => setEmail(e.target.value)} type="email" required
                    className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="you@company.com" />
                </div>
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Password</Label>
                  <Input data-testid="login-password" value={password} onChange={e => setPassword(e.target.value)} type="password" required
                    className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Your password" />
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={() => { setForgotEmail(email); setMode('forgot'); }}
                    className="text-xs text-[#00FF99] hover:underline" data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>
                <Button type="submit" data-testid="login-submit" disabled={loading}
                  className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] rounded-md h-11">
                  <LogIn size={18} className="mr-2" />
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </>
          )}

          {mode === '2fa' && (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <p className="text-[#B0B0B0] text-sm text-center">
                {twoFaMethod === 'email'
                  ? 'Enter the 6-digit code we just emailed to you'
                  : 'Enter the 6-digit code from your authenticator app'}
              </p>
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">
                  {twoFaMethod === 'email' ? 'Email Code' : 'Authentication Code'}
                </Label>
                <Input data-testid="2fa-code" value={code} onChange={e => setCode(e.target.value)} required
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1 text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000" maxLength={6} autoFocus />
              </div>
              <Button type="submit" data-testid="2fa-submit" disabled={loading}
                className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] rounded-md h-11">
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
              {twoFaMethod === 'email' && (
                <button type="button" onClick={handleResendCode} disabled={loading}
                  className="w-full text-sm text-[#00FF99] hover:underline" data-testid="resend-2fa">
                  Didn't get it? Resend code
                </button>
              )}
              <button type="button" onClick={() => { setMode('login'); setCode(''); }}
                className="w-full text-sm text-[#808080] hover:text-white">
                Back to login
              </button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-[#B0B0B0] text-sm text-center">
                Enter your email and we'll send you a reset link
              </p>
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Email</Label>
                <Input data-testid="forgot-email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} type="email" required
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="you@company.com" autoFocus />
              </div>
              <Button type="submit" data-testid="forgot-submit" disabled={loading}
                className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] rounded-md h-11">
                <Mail size={18} className="mr-2" />
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <button type="button" onClick={() => setMode('login')}
                className="w-full text-sm text-[#808080] hover:text-white" data-testid="back-to-login">
                Back to login
              </button>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-[rgba(255,255,255,0.12)]">
            <p className="text-xs text-[#808080] text-center">
              Telecom &middot; Insurance &middot; Real Estate
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
